import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Button, Tab, Tabs as MaterialTabs } from '@material-ui/core'
import styles from './Tabs.module.css'
import { COLOR_PRIMARY, COLOR_SETTINGS } from '../../routes/color-constants'
import { AppTable, Field } from '../../components/table'
import { Actions, Pager, Sort } from '../../components/table/types'
import editIcon from '../../assets/table-icons/edit-icon.svg'
import { Query, QueryParam, SortParam } from '../../common/api/Query'
import { useStyles } from '../matchings/Matching.styles'
import { navigate } from '@reach/router'
import { URL_PARAMETER } from '../../routes/routes-constants'
import { PatientParam } from '../../modules/patients/models/PatientParam'
import { getPatientContainer } from '../../container/patient-module'
import { PatientParamService } from '../../modules/patients/services/PatientParamService'
import { PATIENT_PARAM_SERVICE_KEY } from '../../modules/patients'

const parameters = ['eyeColor', 'hairColor', 'skinColor', 'ethnicity']

const patientParamService =
  getPatientContainer().get<PatientParamService>(PATIENT_PARAM_SERVICE_KEY)

export const Tabs = () => {
  const { t } = useTranslation()
  const classes = useStyles({ color: COLOR_PRIMARY })

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [pager, setPager] = useState<Pager>()
  const [page, setPage] = useState<number>(0)
  const [count, setCount] = useState<number>(0)
  const [itemsPerPage, setItemsPerPage] = useState<number>(10)
  const [items, setItems] = useState<PatientParam[]>([])
  const [sort, setSort] = useState<SortParam<PatientParam>>({
    field: 'type',
    desc: true,
  })
  const [tabValue, setTabValue] = useState<number>(0)

  useEffect(() => {
    if (!isLoading) {
      return
    }
    patientParamService
      .getFilteredList(
        new Query({
          pager: { offset: page * itemsPerPage, limit: itemsPerPage },
          sort: [{ field: sort.field, desc: sort.desc }],
          query: [new QueryParam<PatientParam>('type', tabValue + 1)],
        })
      )
      .subscribe((res) => {
        setIsLoading(false)
        setItems(res.items)
        setCount(res.count)
      })
  }, [isLoading])

  useEffect(() => {
    setIsLoading(true)
    setPager({
      page,
      count,
      handleChangePage: handlePaginationChange,
      rowsPerPage: itemsPerPage,
      handleChangeRowsPerPage,
    })
  }, [page, count, itemsPerPage])

  const changeTabValue = (event: React.ChangeEvent<{}>, tabNumber: number) => {
    setIsLoading(true)
    setTabValue(tabNumber)
  }

  const handlePaginationChange = (event: unknown, value: number) => setPage(value)

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (Number.isNaN(event.target.value)) {
      setItemsPerPage(10)
      return
    }
    setItemsPerPage(Number.parseInt(event.target.value))
  }

  const editParameter = (patientParam: PatientParam) =>
    navigate(URL_PARAMETER.replace(':id', `${patientParam.id}`))

  const actions: Actions<PatientParam> = {
    actionsColumn: t('Actions'),
    items: [
      {
        handler: editParameter,
        icon: editIcon,
        label: t('Edit'),
      },
    ],
  }

  const fields: Field<PatientParam>[] = [
    {
      sortable: true,
      label: t('value'),
      name: 'value',
    },
  ]

  const sortable: Sort<PatientParam> = {
    name: sort.field,
    direction: sort.desc ? 'desc' : 'asc',
    handleSort: (field) => {
      setSort({ field: field, desc: sort.field === field ? !sort.desc : true })
      setIsLoading(true)
    },
  }
  const createTranslation = () => navigate(URL_PARAMETER.replace(':id', 'create'))

  return (
    <Box flexGrow={'1'} display={'flex'}>
      <MaterialTabs
        orientation={'vertical'}
        variant={'scrollable'}
        indicatorColor={'primary'}
        value={tabValue}
        onChange={changeTabValue}
        className={styles.tabs}>
        {parameters.map((param) => (
          <Tab classes={{ wrapper: styles.tab }} key={param} label={t(param)} />
        ))}
      </MaterialTabs>
      <Box style={{ width: '100%' }}>
        <Box justifyContent={'space-between'} mb={2}>
          <Button
            className={classes.button}
            onClick={createTranslation}
            variant={'contained'}
            style={{ float: 'right' }}>
            {t('create')}
          </Button>
        </Box>
        <AppTable
          styleHeader={{ color: COLOR_SETTINGS }}
          actions={actions}
          fields={fields}
          items={items}
          rowKeyField={'id'}
          pager={pager}
          sort={sortable}
        />
      </Box>
    </Box>
  )
}
